import { BaseFrontdoorAdapter } from "../frontdoor/BaseFrontdoorAdapter";

/**
 * FrontdoorThemeAdapter
 * Manages platform-level Theme operations via the Frontdoor REST API.
 * Endpoints sourced from: api_blueprint.md > Themes
 * Updated per swagger: https://smarthubfrontdoor.apexfeature9.xyz/swagger/v1/swagger.json
 */
export class FrontdoorThemeAdapter extends BaseFrontdoorAdapter {
  /**
   * Fetch all available themes.
   * GET /api/themes
   * @returns {Promise<Array>} Array of ThemeDto objects
   */
  async getAllThemes() {
    return this.get("/api/themes");
  }

  /**
   * Fetch a theme by ID.
   * GET /api/themes/{id}
   * @param {string} id - Theme UUID
   */
  async getById(id) {
    return this.get(`/api/themes/${id}`);
  }

  /**
   * Create a new theme.
   * POST /api/themes
   * @param {Object} payload - Theme data { color, logoUrl }
   */
  async create(payload) {
    return this.post("/api/themes", payload);
  }

  /**
   * Update an existing theme.
   * PUT /api/themes/{themeId}
   * @param {string} id - Theme UUID
   * @param {Object} payload - Theme data { color, logoUrl }
   */
  async update(id, payload) {
    return this.put(`/api/themes/${id}`, payload);
  }

  /**
   * Delete a theme.
   * DELETE /api/themes/{themeId}
   * @param {string} id - Theme UUID
   */
  async delete(id) {
    return super.delete(`/api/themes/${id}`);
  }

  /**
   * Upload a logo image for a theme.
   * PUT /api/themes/{id}/logo
   * @param {string} id - Theme ID (UUID)
   * @param {FormData} formData - FormData containing the logo file
   */
  async uploadLogo(id, formData) {
    return this.putMultipart(`/api/themes/${id}/logo`, formData);
  }

  /**
   * Get the current user's theme preference.
   * GET /api/themes/user/current
   * @returns {Promise<ThemeDto>} Current user's theme
   */
  async getUserCurrentTheme() {
    return this.get("/api/themes/user/current");
  }

  /**
   * Set the current user's theme preference.
   * PUT /api/themes/user/{themeId}
   * @param {string} themeId - Theme UUID to set for the user
   */
  async setUserTheme(themeId) {
    return this.put(`/api/themes/user/${themeId}`);
  }
}
